import type { ObjectData } from './schema';

/** A copied style (copy/paste style): the main color, the supported style keys, a text's size. */
export type StyleClip = { color?: string; style: Record<string, unknown>; fontSize?: number };

const STYLE_KEYS = ['fill', 'stroke', 'strokeWidth', 'color', 'opacity'] as const;
/** Style keys each type accepts besides its main color. */
const ACCEPTS: Partial<Record<ObjectData['type'], readonly string[]>> = {
  shape: ['stroke', 'strokeWidth', 'opacity'],
  connector: ['strokeWidth', 'opacity'],
  text: ['opacity'],
  sticky: ['opacity'],
  section: ['opacity'],
};

const str = (v: unknown) => (typeof v === 'string' ? v : undefined);

/** The main color of an object: a sticky's color, a section's fill, a shape's fill, a text's color, a connector's stroke. */
function colorOf(o: Readonly<ObjectData>): string | undefined {
  if (o.type === 'sticky') return o.color;
  if (o.type === 'section') return o.fill;
  if (o.type === 'shape') return str(o.style.fill);
  if (o.type === 'text') return str(o.style.color);
  if (o.type === 'connector') return str(o.style.stroke);
  return undefined;
}

export function styleOf(o: Readonly<ObjectData>): StyleClip {
  const style: Record<string, unknown> = {};
  for (const k of STYLE_KEYS) if (o.style[k] !== undefined) style[k] = o.style[k];
  const color = colorOf(o);
  return { ...(color !== undefined ? { color } : {}), style, ...(o.type === 'text' ? { fontSize: o.fontSize } : {}) };
}

/** What of `clip` applies to `o`: document props (sticky color, section fill, text size) and style keys. */
export function stylePatch(clip: StyleClip, o: Readonly<ObjectData>): { props: Record<string, unknown>; style: Record<string, unknown> } {
  const props: Record<string, unknown> = {};
  const style: Record<string, unknown> = {};
  for (const k of ACCEPTS[o.type] ?? []) if (clip.style[k] !== undefined) style[k] = clip.style[k];
  if (clip.color !== undefined) {
    if (o.type === 'sticky') props.color = clip.color;
    else if (o.type === 'section') props.fill = clip.color;
    else if (o.type === 'shape') style.fill = clip.color;
    else if (o.type === 'text') style.color = clip.color;
    else if (o.type === 'connector') style.stroke = clip.color;
  }
  if (o.type === 'text' && clip.fontSize !== undefined) props.fontSize = clip.fontSize;
  return { props, style };
}
